import { createContext, useContext, useState, useMemo, useEffect, useCallback } from "react";
import { SALES_TEAM } from "./seed";
import {
  BASE_SALARY, UNPLANNED_ABSENCE_PENALTY, DEFAULT_OFFICE_TIMING, BILLING_MONTHS,
  summariseAttendance, computePayroll,
} from "./attendance";
import { SEED_BOOKINGS, SEED_EXPENSES, SEED_GST_PAYMENTS } from "./billing";
import { bookingsApi, monthLabelToYM, ymToMonthLabel } from "./bookingsApi";
import { employeesApi } from "./employeesApi";
import { attendanceApi, adaptByName } from "./attendanceApi";
import { salaryAdvancesApi } from "./salaryAdvancesApi";
import { officeTimingApi } from "./officeTimingApi";
import { expensesApi } from "./expensesApi";
import { gstApi } from "./gstApi";
import { INTERIOR_LEADS } from "./interior";
import { BLOG_POSTS } from "./blog";
import { leadsApi, listActiveEmployees } from "./leadsApi";
import { citiesApi, localitiesApi, buildersApi } from "./localitiesApi";
import { propertiesApi } from "./propertiesApi";
import { leadStatusesApi } from "./leadStatusesApi";
// setLeadStatuses replaces the in-memory registry in lib/leads.js with the
// server's list. STATUSES / STATUS_META are `let` exports, so ES module live
// bindings propagate the reassignment to every importer (StatusBadge,
// LeadsList, LeadDetail, KanbanBoard, LeadForm, LeadSettings) with no changes
// needed in those files.
import { setLeadStatuses } from "./leads";

// Phase 4 — API clients
import { interiorLeadsApi } from "./interiorLeadsApi";
import { blogApi } from "./blogApi";
import { siteSettingsApi } from "./siteSettingsApi";

// Phase 4.5 — Interior CMS clients
import { designersApi, packagesApi, portfolioApi, pageContentApi } from "./interiorCmsApi";
import { salaryHistoryApi, salaryFor } from "./salaryHistoryApi";

const AppDataContext = createContext(null);

function buildSeedAttendanceLog() {
  const log = {};
  SALES_TEAM.forEach((m) => { log[m.name] = []; });
  log["Sarika K."] = [
    { date: "2026-04-01", status: "present",    clockInTime: "09:42", clockOutTime: "18:35", selfie: null, markedBy: "self" },
    { date: "2026-04-02", status: "present",    clockInTime: "09:51", clockOutTime: "18:40", selfie: null, markedBy: "self" },
    { date: "2026-04-03", status: "present",    clockInTime: "09:38", clockOutTime: "18:25", selfie: null, markedBy: "self" },
    { date: "2026-04-04", status: "half_day",   clockInTime: "10:18", clockOutTime: "18:30", selfie: null, markedBy: "self" },
    { date: "2026-04-06", status: "present",    clockInTime: "09:45", clockOutTime: "18:40", selfie: null, markedBy: "self" },
    { date: "2026-04-07", status: "leave",      reason: "Personal",   markedBy: "admin" },
    { date: "2026-04-08", status: "present",    clockInTime: "09:30", clockOutTime: "18:50", selfie: null, markedBy: "self" },
  ];
  log["Kedar Naik"] = [
    { date: "2026-04-01", status: "present",    clockInTime: "09:55", clockOutTime: "18:30", markedBy: "self" },
    { date: "2026-04-02", status: "absent",     reason: "No-show",    markedBy: "admin" },
    { date: "2026-04-03", status: "present",    clockInTime: "09:48", clockOutTime: "18:35", markedBy: "self" },
    { date: "2026-04-04", status: "half_day",   clockInTime: "10:32", clockOutTime: "18:30", markedBy: "self" },
    { date: "2026-04-06", status: "absent",     reason: "No-show",    markedBy: "admin" },
    { date: "2026-04-07", status: "present",    clockInTime: "09:40", clockOutTime: "18:45", markedBy: "self" },
  ];
  log["Riya Mehta"] = [
    { date: "2026-04-01", status: "present",    clockInTime: "09:25", clockOutTime: "18:32", markedBy: "self" },
    { date: "2026-04-02", status: "present",    clockInTime: "09:32", clockOutTime: "18:28", markedBy: "self" },
    { date: "2026-04-03", status: "paid_leave", reason: "Approved PTO", markedBy: "admin" },
    { date: "2026-04-04", status: "present",    clockInTime: "09:35", clockOutTime: "18:40", markedBy: "self" },
    { date: "2026-04-06", status: "present",    clockInTime: "09:40", clockOutTime: "18:35", markedBy: "self" },
    { date: "2026-04-07", status: "present",    clockInTime: "09:33", clockOutTime: "18:42", markedBy: "self" },
  ];
  log["Aditya Rao"] = [
    { date: "2026-04-01", status: "half_day",   clockInTime: "10:15", clockOutTime: "18:30", markedBy: "self" },
    { date: "2026-04-02", status: "half_day",   clockInTime: "10:22", clockOutTime: "18:35", markedBy: "self" },
    { date: "2026-04-03", status: "half_day",   clockInTime: "10:08", clockOutTime: "18:30", markedBy: "self" },
    { date: "2026-04-04", status: "present",    clockInTime: "09:58", clockOutTime: "18:40", markedBy: "self" },
    { date: "2026-04-06", status: "present",    clockInTime: "09:50", clockOutTime: "18:30", markedBy: "self" },
  ];
  return log;
}

export function AppDataProvider({ children }) {
  // ═══════════════════════════════════════════════════════════════════
  //  Phase 3 — Cities, Localities, Builders, Properties — API-backed
  // ═══════════════════════════════════════════════════════════════════

  const [cities, setCities] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [builders, setBuilders] = useState([]);
  const [properties, setProperties] = useState([]);

  const [mastersLoading, setMastersLoading] = useState(true);
  // Set true by Add/Edit forms (Properties, Leads, Bookings) while they
  // hold unsaved changes; the shell guards section-switching against it.
  const [dirtyForm, setDirtyForm] = useState(false);
  const [propertiesLoading, setPropertiesLoading] = useState(true);

  const refreshMasters = useCallback(async () => {
    try {
      const [c, l, b] = await Promise.all([
        citiesApi.list(true),
        localitiesApi.list({ includeInactive: true }),
        buildersApi.list(true),
      ]);
      setCities(c);
      setLocalities(l);
      setBuilders(b);
    } catch (err) {
      console.error("[masters] load failed:", err);
    } finally {
      setMastersLoading(false);
    }
  }, []);

  const refreshProperties = useCallback(async () => {
    try {
      const r = await propertiesApi.list({ pageSize: 200 });
      setProperties(r.items);
    } catch (err) {
      console.error("[properties] load failed:", err);
    } finally {
      setPropertiesLoading(false);
    }
  }, []);

  // ── Cities CRUD ──
  const addCity = async (data) => {
    const c = await citiesApi.create(data);
    setCities((p) => [...p, c]);
    return c;
  };
  const updateCity = async (id, patch) => {
    const c = await citiesApi.update(id, patch);
    setCities((p) => p.map((x) => x.id === id ? { ...x, ...c } : x));
    return c;
  };
  const toggleCityActive = async (id) => {
    const city = cities.find((c) => c.id === id);
    if (!city) return;
    return updateCity(id, { active: !city.active });
  };
  const deleteCity = async (id) => {
    await citiesApi.delete(id);
    setCities((p) => p.filter((c) => c.id !== id));
  };

  // ── Localities CRUD ──
  const addLocality = async (data) => {
    const l = await localitiesApi.create(data);
    setLocalities((p) => [...p, l]);
    return l;
  };
  const updateLocality = async (id, patch) => {
    const l = await localitiesApi.update(id, patch);
    setLocalities((p) => p.map((x) => x.id === id ? { ...x, ...l } : x));
    return l;
  };
  const toggleLocalityActive = async (id) => {
    const loc = localities.find((l) => l.id === id);
    if (!loc) return;
    return updateLocality(id, { active: !loc.active });
  };
  const deleteLocality = async (id) => {
    await localitiesApi.delete(id);
    setLocalities((p) => p.filter((l) => l.id !== id));
  };

  // ── Builders CRUD ──
  const addBuilder = async (data) => {
    const b = await buildersApi.create(data);
    setBuilders((p) => [...p, b]);
    return b;
  };
  const updateBuilder = async (id, patch) => {
    const b = await buildersApi.update(id, patch);
    setBuilders((p) => p.map((x) => x.id === id ? { ...x, ...b } : x));
    return b;
  };
  const deleteBuilder = async (id) => {
    await buildersApi.delete(id);
    setBuilders((p) => p.filter((b) => b.id !== id));
  };
  const reorderBuilder = async (id, direction) => {
    const idx = builders.findIndex((b) => b.id === id);
    if (idx === -1) return;
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= builders.length) return;
    const next = [...builders];
    [next[idx], next[target]] = [next[target], next[idx]];
    setBuilders(next);
    try {
      await buildersApi.reorder(next.map((b) => b.id));
    } catch (err) {
      setBuilders(builders); // rollback
      throw err;
    }
  };

  // ── Properties CRUD ──
  const addProperty = async (data) => {
    const p = await propertiesApi.create(data);
    setProperties((prev) => [p, ...prev]);
    return p;
  };
  const updateProperty = async (id, patch) => {
    const p = await propertiesApi.update(id, patch);
    setProperties((prev) => prev.map((x) => x.id === id ? { ...x, ...p } : x));
    return p;
  };
  const deleteProperty = async (id) => {
    await propertiesApi.delete(id);
    setProperties((prev) => prev.filter((p) => p.id !== id));
  };
  const duplicateProperty = async (id) => {
    const p = await propertiesApi.duplicate(id);
    setProperties((prev) => [p, ...prev]);
    return p;
  };

  useEffect(() => {
    refreshMasters();
    refreshProperties();
  }, [refreshMasters, refreshProperties]);

  // ═══════════════════════════════════════════════════════════════════
  //  Sales Team (mock — billing/payroll/attendance only, Phase 5)
  // ═══════════════════════════════════════════════════════════════════
  const [seedOverrides, setSeedOverrides] = useState({});
  const [addedSalesPeople, setAddedSalesPeople] = useState([]);
  const [deletedSeedIds, setDeletedSeedIds] = useState(new Set());

  const overrideSalesPerson = (id, patch) =>
    setSeedOverrides((prev) => ({
      ...prev,
      [id]: { ...patch, id, profile: { ...(prev[id]?.profile || {}), ...(patch.profile || {}) } },
    }));
  const addSalesPerson = (p) => setAddedSalesPeople((prev) => [...prev, p]);
  const updateAddedSalesPerson = (id, patch) =>
    setAddedSalesPeople((prev) => prev.map((p) => p.id === id ? { ...p, ...patch, id } : p));
  const deleteSalesPerson = async (id) => {
    try {
      await employeesApi.archive(id);
      await refreshEmployees();
    } catch (e) {
      console.error("[employees] archive failed", e);
      throw e;
    }
  };

  const isSeededSalesPerson = (_id) => false;  // Phase 5: API-only
  const upsertSalesPerson = async (record, isNew) => {
    try {
      if (isNew || !record.id) {
        const created = await employeesApi.create(record);
        await refreshEmployees();
        return created;
      }
      const updated = await employeesApi.update(record.id, record);
      await refreshEmployees();
      return updated;
    } catch (e) {
      console.error("[employees] upsert failed", e);
      throw e;
    }
  };

  const [attendanceLog, setAttendanceLog] = useState({});  // Phase 5: API-backed, keyed by employee name
  const markAttendance = async (employeeNameOrId, dateOrEntry, statusArg, noteArg) => {
    // Accept BOTH old and new calling shapes:
    //   markAttendance(name, "2026-04-15", "absent", "reason")        (new 4-arg form)
    //   markAttendance(name, { date, status, note?, clockInAt?, ... }) (old 2-arg form)
    let date, status, note, clockInAt, clockOutAt;
    if (dateOrEntry && typeof dateOrEntry === "object") {
      // Old-style entry object
      date       = dateOrEntry.date;
      status     = dateOrEntry.status;
      note       = dateOrEntry.note || dateOrEntry.reason || "";
      clockInAt  = dateOrEntry.clockInAt  || null;
      clockOutAt = dateOrEntry.clockOutAt || null;
    } else {
      date   = dateOrEntry;
      status = statusArg;
      note   = noteArg || "";
    }
    if (!date || !status) {
      console.warn("[attendance] missing date or status", { date, status });
      return;
    }
    // Resolve employeeId — accept either an id or a name
    const emp = (activeEmployees || []).find(
      (e) => e.id === employeeNameOrId || e.name === employeeNameOrId,
    );
    if (!emp) {
      console.warn("[attendance] employee not found:", employeeNameOrId);
      return;
    }
    // Optimistic local update
    setAttendanceLog((prev) => {
      const next = { ...prev };
      const list = (next[emp.name] || []).filter((r) => r.date !== date);
      next[emp.name] = [...list, { date, status, note, clockInAt, clockOutAt }];
      next[emp.name].sort((a, b) => a.date.localeCompare(b.date));
      return next;
    });
    try {
      await attendanceApi.upsert({
        employeeId: emp.id,
        date,
        status,
        note,
        clockInAt,
        clockOutAt,
      });
    } catch (e) {
      console.error("[attendance] upsert failed", e);
      // Roll back by reloading the whole month
      reloadAttendance(billingMonth);
    }
  };

  // Phase 5 Bundle C — self-service clock in/out for the logged-in user.
  // Uploads selfie via API and writes attendance row in one shot.
  const clockInSelf = async ({ date, clockInAt, selfieDataUrl, location, note }) => {
    try {
      const entry = await attendanceApi.clockIn({ date, clockInAt, selfieDataUrl, location, note });
      // Refresh attendance for the active month so UI updates
      reloadAttendance(billingMonth);
      return entry;
    } catch (e) {
      console.error("[attendance] clockIn failed", e);
      throw e;
    }
  };

  const clockOutSelf = async ({ date, clockOutAt, selfieDataUrl, location }) => {
    try {
      const entry = await attendanceApi.clockOut({ date, clockOutAt, selfieDataUrl, location });
      reloadAttendance(billingMonth);
      return entry;
    } catch (e) {
      console.error("[attendance] clockOut failed", e);
      throw e;
    }
  };

  const markAbsentSelf = async ({ date, reason }) => {
    try {
      const entry = await attendanceApi.markAbsentSelf({ date, reason });
      reloadAttendance(billingMonth);
      return entry;
    } catch (e) {
      console.error("[attendance] markAbsentSelf failed", e);
      throw e;
    }
  };
  const [officeTiming, setOfficeTimingState] = useState(DEFAULT_OFFICE_TIMING);

  // Phase 5 Bundle C — load office timing from API + persist on change.
  useEffect(() => {
    (async () => {
      try {
        const t = await officeTimingApi.get();
        if (t) setOfficeTimingState(t);
      } catch (e) { console.warn("[office-timing] load failed", e); }
    })();
  }, []);

  const setOfficeTiming = async (next) => {
    setOfficeTimingState(next);
    try {
      await officeTimingApi.update(next);
    } catch (e) {
      console.error("[office-timing] save failed", e);
    }
  };

  const [billingMonth, setBillingMonth] = useState(() => {
    // Phase 5 Bundle A — default to current month so today's attendance shows
    const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const now = new Date();
    return MONTHS[now.getMonth()] + " " + now.getFullYear();
  });
  const [salaryAdvances, setSalaryAdvances] = useState({});
  const addSalaryAdvance = async (entry) => {
    // entry: { date, amount, note, employeeId? or employeeName? }
    const emp = (activeEmployees || []).find(
      (e) => e.id === entry.employeeId || e.name === entry.employeeName,
    );
    if (!emp && !entry.employeeId) {
      console.warn("[salary-advances] no employee specified");
      return;
    }
    try {
      const created = await salaryAdvancesApi.create({
        employeeId: emp?.id || entry.employeeId,
        date: entry.date || new Date().toISOString().slice(0, 10),
        amount: entry.amount,
        note: entry.note || "",
        billingMonth: billingMonth,
      });
      setSalaryAdvances((prev) => ({
        ...prev,
        [billingMonth]: [created, ...(prev[billingMonth] || [])],
      }));
      return created;
    } catch (e) {
      console.error("[salary-advances] create failed", e);
      throw e;
    }
  };
  const updateSalaryAdvance = async (id, patch) => {
    try {
      const updated = await salaryAdvancesApi.update(id, patch);
      setSalaryAdvances((prev) => ({
        ...prev,
        [billingMonth]: (prev[billingMonth] || []).map((a) => a.id === id ? { ...a, ...updated } : a),
      }));
      return updated;
    } catch (e) {
      console.error("[salary-advances] update failed", e);
      throw e;
    }
  };
  const deleteSalaryAdvance = async (id) => {
    try {
      await salaryAdvancesApi.delete(id);
      setSalaryAdvances((prev) => ({
        ...prev,
        [billingMonth]: (prev[billingMonth] || []).filter((a) => a.id !== id),
      }));
    } catch (e) {
      console.error("[salary-advances] delete failed", e);
      throw e;
    }
  };
  const activeAdvances = (salaryAdvances[billingMonth] || []).slice().sort((a, b) => (b.date || "").localeCompare(a.date || ""));


  // ═══════════════════════════════════════════════════════════════════
  //  Phase 2 — Leads (API-backed)
  // ═══════════════════════════════════════════════════════════════════

  const [leadsData, setLeadsData] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [leadsError, setLeadsError] = useState(null);
  const [activeEmployees, setActiveEmployees] = useState([]);

  // Phase 5 Bundle A — allSalesPeople derives from API-backed activeEmployees.
  const allSalesPeople = activeEmployees || [];

  const refreshLeads = useCallback(async () => {
    try {
      const r = await leadsApi.list({ pageSize: 200 });
      setLeadsData(r.leads);
      setLeadsError(null);
    } catch (err) {
      setLeadsError(err?.message || "Failed to load leads");
    } finally {
      setLeadsLoading(false);
    }
  }, []);

  const refreshEmployees = useCallback(async () => {
    try {
      const { items } = await employeesApi.list({ active: "all" });
      setActiveEmployees(items);
    } catch (e) {
      console.warn("[employees] refresh failed", e);
      setActiveEmployees([]);
    }
  }, []);

  // Performance targets are API-backed: each employee row carries
  // visitTarget/tokenTarget/bookingTarget. We derive a { [id]: {...} } map
  // from allSalesPeople and keep an optimistic overlay for instant typing.
  /* Salary is per-MONTH, not one current figure -- see salaryHistoryApi.
     null until loaded, so a mid-load render holds the previous number rather
     than flashing zero. Once loaded, a missing row genuinely means nobody
     was paid that month: someone who joined in December was not on the books
     in September, and September must not show a cost for them. */
  const [salaryRows, setSalaryRows] = useState(null);

  const [perfTargetOverlay, setPerfTargetOverlay] = useState({});
  const perfTargets = useMemo(() => {
    const base = {};
    for (const p of (activeEmployees || [])) {
      base[p.id] = {
        visitTarget: p.visitTarget ?? 0,
        tokenTarget: p.tokenTarget ?? 0,
        bookingTarget: p.bookingTarget ?? 0,
      };
    }
    // Overlay any in-flight local edits on top of API values.
    for (const [id, patch] of Object.entries(perfTargetOverlay)) {
      base[id] = { ...(base[id] || { visitTarget: 0, tokenTarget: 0, bookingTarget: 0 }), ...patch };
    }
    return base;
  }, [activeEmployees, perfTargetOverlay]);

  // Optimistic local update (instant UI); persistence happens on commit.
  const setPerfTarget = (id, key, value) => {
    const v = Math.max(0, parseInt(value) || 0);
    setPerfTargetOverlay((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [key]: v },
    }));
  };

  // Persist a person's edited targets to the API, then refresh + clear overlay.
  const commitPerfTargets = async (id) => {
    const patch = perfTargetOverlay[id];
    if (!patch) return;
    try {
      await employeesApi.update(id, {
        visitTarget: patch.visitTarget,
        tokenTarget: patch.tokenTarget,
        bookingTarget: patch.bookingTarget,
      });
      await refreshEmployees();
      setPerfTargetOverlay((prev) => { const { [id]: _drop, ...rest } = prev; return rest; });
    } catch (e) {
      console.error("[perf] target save failed", e);
      throw e;
    }
  };

  // Commit ALL pending target edits (used by the Done button).
  const commitAllPerfTargets = async () => {
    const ids = Object.keys(perfTargetOverlay);
    for (const id of ids) {
      const patch = perfTargetOverlay[id];
      try {
        await employeesApi.update(id, {
          visitTarget: patch.visitTarget,
          tokenTarget: patch.tokenTarget,
          bookingTarget: patch.bookingTarget,
        });
      } catch (e) {
        console.error("[perf] target save failed for", id, e);
      }
    }
    if (ids.length) await refreshEmployees();
    setPerfTargetOverlay({});
  };


  /* Pull the status list from the server and push it into the lib/leads.js
     registry. Runs at boot and after any Lead Settings mutation.

     Previously the registry was purely in-memory, so a status created in Lead
     Settings survived navigation but vanished on refresh — nothing was ever
     persisted. It now lives in the lead_statuses table.

     On failure the builtin ten stay in place, so the leads screens keep
     working even if this request fails. */
  const refreshLeadStatuses = useCallback(async () => {
    try {
      const rows = await leadStatusesApi.list();
      if (setLeadStatuses(rows)) bumpStatusVersion();
    } catch (e) {
      console.warn("[lead-statuses] load failed, using builtin defaults", e);
    }
  }, []);

  useEffect(() => {
    refreshLeads();
    refreshEmployees();
    refreshLeadStatuses();
  }, [refreshLeads, refreshEmployees, refreshLeadStatuses]);

  useEffect(() => {
    const aModalIsOpen = () => document.body.style.overflow === "hidden";
    const onVisible = () => {
      if (document.visibilityState === "visible" && !aModalIsOpen()) {
        refreshLeads();
        refreshProperties();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    const pollId = setInterval(() => {
      if (document.visibilityState === "visible" && !aModalIsOpen()) {
        refreshLeads();
      }
    }, 30_000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      clearInterval(pollId);
    };
  }, [refreshLeads, refreshProperties]);

  const addLead = async (uiLead) => {
    try {
      const created = await leadsApi.create(uiLead);
      setLeadsData((prev) => [created, ...prev]);
      return created;
    } catch (err) {
      setLeadsError(err?.message || "Failed to add lead");
      throw err;
    }
  };
  const updateLead = async (patched) => {
    try {
      const updated = await leadsApi.update(patched.id, patched);
      setLeadsData((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
      return updated;
    } catch (err) {
      setLeadsError(err?.message || "Failed to update lead");
      throw err;
    }
  };
  const deleteLead = async (id) => {
    try {
      await leadsApi.delete(id);
      setLeadsData((prev) => prev.filter((l) => l.id !== id));
    } catch (err) {
      setLeadsError(err?.message || "Failed to delete lead");
      throw err;
    }
  };
  const addLeadNote = async (leadId, body) => leadsApi.addNote(leadId, body);
  const bulkLeadAction = async (payload) => {
    const res = await leadsApi.bulk(payload);
    await refreshLeads();
    return res;
  };
  const importLeadsCsv = async (csvText) => {
    const res = await leadsApi.importCsv(csvText);
    await refreshLeads();
    return res;
  };
  const exportLeadsCsv = async (filters = {}) => leadsApi.exportCsv(filters);

  const [statusVersion, setStatusVersion] = useState(0);
  const bumpStatusVersion = () => setStatusVersion((v) => v + 1);

  // ═══════════════════════════════════════════════════════════════════
  //  Interior leads / Blog posts (Phase 4 — API-backed)
  //  Variable names preserved (interiorLeads / posts) so existing views
  //  don't need import changes.
  // ═══════════════════════════════════════════════════════════════════
  const [interiorLeads, setInteriorLeads] = useState([]);
  const [interiorLeadsLoaded, setInteriorLeadsLoaded] = useState(false);

  const reloadInteriorLeads = useCallback(async () => {
    try {
      const res = await interiorLeadsApi.list({ pageSize: 200 });
      setInteriorLeads(res.items || []);
      setInteriorLeadsLoaded(true);
    } catch (e) {
      console.error("Failed to load interior leads:", e);
      setInteriorLeadsLoaded(true);
    }
  }, []);

  const addInteriorLead = async (payload) => {
    const created = await interiorLeadsApi.create(payload);
    setInteriorLeads((prev) => [created, ...prev]);
    return created;
  };
  const updateInteriorLead = async (patched) => {
    const updated = await interiorLeadsApi.update(patched.id, patched);
    setInteriorLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    return updated;
  };
  const deleteInteriorLead = async (id) => {
    await interiorLeadsApi.delete(id);
    setInteriorLeads((prev) => prev.filter((l) => l.id !== id));
  };

  const [posts, setPosts] = useState([]);
  const [postsLoaded, setPostsLoaded] = useState(false);

  const reloadPosts = useCallback(async () => {
    try {
      const res = await blogApi.list({ pageSize: 200 });
      setPosts(res.items || []);
      setPostsLoaded(true);
    } catch (e) {
      console.error("Failed to load blog posts:", e);
      setPostsLoaded(true);
    }
  }, []);

  const addPost = async (payload) => {
    const created = await blogApi.create(payload);
    setPosts((prev) => [created, ...prev]);
    return created;
  };
  const updatePost = async (payload) => {
    const updated = await blogApi.update(payload.id, payload);
    setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    return updated;
  };
  const deletePost = async (id) => {
    await blogApi.delete(id);
    setPosts((prev) => prev.filter((p) => p.id !== id));
  };

  // ═══════════════════════════════════════════════════════════════════
  //  Phase 4.5 — Interior CMS (API-backed)
  //  designers / packages / portfolio / page content
  // ═══════════════════════════════════════════════════════════════════
  const [interiorDesigners, setInteriorDesigners] = useState([]);
  const [interiorDesignersLoaded, setInteriorDesignersLoaded] = useState(false);

  const reloadInteriorDesigners = useCallback(async () => {
    try {
      const res = await designersApi.list({ includeInactive: true });
      setInteriorDesigners(res.items || []);
      setInteriorDesignersLoaded(true);
    } catch (e) {
      console.error("Failed to load interior designers:", e);
      setInteriorDesignersLoaded(true);
    }
  }, []);

  const addInteriorDesigner = async (payload) => {
    const created = await designersApi.create(payload);
    setInteriorDesigners((prev) => [...prev, created]);
    return created;
  };
  const updateInteriorDesigner = async (id, payload) => {
    const updated = await designersApi.update(id, payload);
    setInteriorDesigners((prev) => prev.map((d) => (d.id === id ? updated : d)));
    return updated;
  };
  const deleteInteriorDesigner = async (id) => {
    await designersApi.delete(id);
    setInteriorDesigners((prev) => prev.filter((d) => d.id !== id));
  };

  const [interiorPackages, setInteriorPackages] = useState([]);
  const [interiorPackagesLoaded, setInteriorPackagesLoaded] = useState(false);

  const reloadInteriorPackages = useCallback(async () => {
    try {
      const res = await packagesApi.list({ includeInactive: true });
      setInteriorPackages(res.items || []);
      setInteriorPackagesLoaded(true);
    } catch (e) {
      console.error("Failed to load interior packages:", e);
      setInteriorPackagesLoaded(true);
    }
  }, []);

  const addInteriorPackage = async (payload) => {
    const created = await packagesApi.create(payload);
    setInteriorPackages((prev) => [...prev, created]);
    return created;
  };
  const updateInteriorPackage = async (id, payload) => {
    const updated = await packagesApi.update(id, payload);
    setInteriorPackages((prev) => prev.map((p) => (p.id === id ? updated : p)));
    return updated;
  };
  const deleteInteriorPackage = async (id) => {
    await packagesApi.delete(id);
    setInteriorPackages((prev) => prev.filter((p) => p.id !== id));
  };

  const [interiorPortfolio, setInteriorPortfolio] = useState([]);
  const [interiorPortfolioLoaded, setInteriorPortfolioLoaded] = useState(false);

  const reloadInteriorPortfolio = useCallback(async () => {
    try {
      const res = await portfolioApi.list({ includeInactive: true, pageSize: 200 });
      setInteriorPortfolio(res.items || []);
      setInteriorPortfolioLoaded(true);
    } catch (e) {
      console.error("Failed to load interior portfolio:", e);
      setInteriorPortfolioLoaded(true);
    }
  }, []);

  const addInteriorPortfolio = async (payload) => {
    const created = await portfolioApi.create(payload);
    setInteriorPortfolio((prev) => [...prev, created]);
    return created;
  };
  const updateInteriorPortfolio = async (id, payload) => {
    const updated = await portfolioApi.update(id, payload);
    setInteriorPortfolio((prev) => prev.map((p) => (p.id === id ? updated : p)));
    return updated;
  };
  const deleteInteriorPortfolio = async (id) => {
    await portfolioApi.delete(id);
    setInteriorPortfolio((prev) => prev.filter((p) => p.id !== id));
  };

  const [interiorPageContent, setInteriorPageContent] = useState(null);
  const [interiorPageContentLoaded, setInteriorPageContentLoaded] = useState(false);

  const reloadInteriorPageContent = useCallback(async () => {
    try {
      const data = await pageContentApi.get();
      setInteriorPageContent(data);
      setInteriorPageContentLoaded(true);
    } catch (e) {
      console.error("Failed to load interior page content:", e);
      setInteriorPageContentLoaded(true);
    }
  }, []);

  const updateInteriorPageContent = async (payload) => {
    const updated = await pageContentApi.update(payload);
    setInteriorPageContent(updated);
    return updated;
  };

  // Bootstrap Phase 4 data on mount
  useEffect(() => {
    reloadInteriorLeads();
    reloadPosts();
    reloadSiteContact();
    // Phase 4.5
    reloadInteriorDesigners();
    reloadInteriorPackages();
    reloadInteriorPortfolio();
    reloadInteriorPageContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  //  Billing (Phase 5 — still mock)
  // ═══════════════════════════════════════════════════════════════════
  // ─── Phase 5 M1: API-backed bookings ────────────────────────────
  // The shape (month-keyed map) is unchanged from the mock — only the
  // source has switched from SEED_BOOKINGS to the API.
  const [bookings, setBookings] = useState({});
  const [bookingsLoadingMonths, setBookingsLoadingMonths] = useState(new Set());

  const activeBookings = bookings[billingMonth] || [];
  const liveBookings = activeBookings.filter((b) => !b.cancelled);
  const cancelledBookings = activeBookings.filter((b) => b.cancelled);

  /* Resolve a booking by id across EVERY loaded month, not just
     billingMonth. The Bookings Ledger's financial-year view loads twelve
     months at once, so a row on screen may belong to April while
     billingMonth is September -- the five mutations below used to look only
     in the current bucket, hit `if (!current) return`, and silently do
     nothing. Bill status, edit and cancel all appeared dead.

     Monthly view is unchanged: only one month is loaded, so this flattens to
     exactly the same rows it searched before. Ids are cuids, unique across
     months, so a flat scan cannot match the wrong record.

     Linear over all loaded bookings. Fine at this scale (tens per month);
     if the ledger ever holds thousands, index by id instead. */
  const findBooking = (id) => Object.values(bookings).flat().find((b) => b.id === id);

  /* Which month bucket a booking's optimistic update belongs in.
     A booking belongs to the month of its own bookingDate, not whichever
     month the ledger happens to be showing. In the FY view those differ, so
     writing to billingMonth left the edited row stale on screen until a
     manual refresh -- the API call succeeded, the display did not follow.

     bookingApiToUi yields "" for a null date and ymToMonthLabel returns ""
     for anything unparseable, so the fallback keeps a dateless booking in
     the current bucket rather than writing to a garbage key and dropping
     the update silently.

     Monthly view is unaffected: you are viewing the month the booking is
     in, so both expressions give the same label. */
  const monthKeyOf = (b) =>
    ymToMonthLabel(String(b?.bookingDate || "").slice(0, 7)) || billingMonth;

  /** Load bookings for a given month label ("April 2026"). Idempotent. */
  const reloadBookings = useCallback(async (monthLabel) => {
    if (!monthLabel) return;
    setBookingsLoadingMonths((s) => new Set([...s, monthLabel]));
    try {
      const { items } = await bookingsApi.listByMonth(monthLabel);
      setBookings((prev) => ({ ...prev, [monthLabel]: items }));
    } catch (e) {
      console.warn("[bookings] reload failed", e);
    } finally {
      setBookingsLoadingMonths((s) => {
        const next = new Set(s);
        next.delete(monthLabel);
        return next;
      });
    }
  }, []);

  // Auto-load whenever billingMonth changes (and on first mount).
  useEffect(() => {
    reloadBookings(billingMonth);
  }, [billingMonth, reloadBookings]);

  // Phase 5 Bundle A — auto-reload attendance + salary advances for current month
  /* MERGES the fetched month into the store rather than replacing it.
     
     It used to call setAttendanceLog(byName) outright, so the store only
     ever held ONE month -- whichever was last fetched. That made a
     twelve-month payroll impossible: yearlyPnl could compute real payroll
     for the selected month and nothing else, so a past month's figure
     vanished the moment you moved the month picker, and September's payroll
     would have dropped to zero in October.
     
     Merging is safe for every reader: monthlyLogByPerson (1196), History
     (27), Today's Roster (28, 84), Calendar (61) and My Attendance (43) all
     filter or find by date before using a row, so extra months are simply
     never matched.
     
     De-duplicates on date per employee -- re-fetching a month replaces its
     rows instead of appending a second copy. Same shape as the optimistic
     merge in markAttendance above. */
  const reloadAttendance = useCallback(async (monthLabel) => {
    try {
      const rows = await attendanceApi.listByMonth(monthLabel);
      const empList = (activeEmployees || []);
      const byName = adaptByName(rows, empList);
      setAttendanceLog((prev) => {
        const next = { ...prev };
        for (const [name, incoming] of Object.entries(byName)) {
          const dates = new Set((incoming || []).map((r) => r.date));
          const kept = (next[name] || []).filter((r) => !dates.has(r.date));
          next[name] = [...kept, ...(incoming || [])]
            .sort((a, b) => a.date.localeCompare(b.date));
        }
        return next;
      });
    } catch (e) {
      console.warn("[attendance] reload failed", e);
    }
  }, [activeEmployees]);

  const reloadSalaryAdvances = useCallback(async (monthLabel) => {
    try {
      const rows = await salaryAdvancesApi.listByMonth(monthLabel);
      setSalaryAdvances((prev) => ({ ...prev, [monthLabel]: rows }));
    } catch (e) {
      // 403 is expected when an employee is viewing — they can't see global advances.
      // Only warn for genuinely unexpected errors.
      if (e?.status !== 403) console.warn("[salary-advances] reload failed", e);
    }
  }, []);

  useEffect(() => {
    reloadAttendance(billingMonth);
  }, [billingMonth, reloadAttendance]);

  useEffect(() => {
    let alive = true;
    salaryHistoryApi.listAll()
      .then((r) => { if (alive) setSalaryRows(r); })
      .catch(() => { if (alive) setSalaryRows([]); });
    return () => { alive = false; };
  }, []);

  /* The yearly P&L needs every FY month's attendance, because payroll is
     baseSalary MINUS attendance deductions -- a past month cannot be
     reconstructed from salary alone. reloadAttendance now merges, so these
     twelve accumulate alongside the selected month rather than replacing
     it. Runs once on mount; months are re-fetched when billingMonth
     changes anyway. */
  useEffect(() => {
    const now = new Date();
    const fyStart = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
    const names = ["April","May","June","July","August","September",
                   "October","November","December","January","February","March"];
    names.forEach((m, i) => {
      reloadAttendance(`${m} ${i < 9 ? fyStart : fyStart + 1}`);
    });
  }, [reloadAttendance]);

  useEffect(() => {
    reloadSalaryAdvances(billingMonth);
  }, [billingMonth, reloadSalaryAdvances]);


  const addBooking = async (form) => {
    if (!form.clientName || !form.projectName) return false;
    try {
      // The form date is YYYY-MM-DD; the form may not include bookingDate
      // explicitly. Use today as fallback if missing.
      const payload = {
        ...form,
        bookingDate: form.bookingDate || new Date().toISOString().slice(0, 10),
      };
      const created = await bookingsApi.create(payload);
      setBookings((prev) => ({
        ...prev,
        [billingMonth]: [...(prev[billingMonth] || []), created],
      }));
      return true;
    } catch (e) {
      console.error("[bookings] create failed", e);
      return false;
    }
  };

  const toggleBookingFlag = async (id, key) => {
    const current = findBooking(id);
    if (!current) return;
    const next = !current[key];
    // Optimistic local update — then persist.
    setBookings((prev) => ({
      ...prev,
      [monthKeyOf(current)]: (prev[monthKeyOf(current)] || []).map((b) => b.id === id ? { ...b, [key]: next } : b),
    }));
    try {
      await bookingsApi.update(id, { [key]: next });
    } catch (e) {
      // Roll back on failure
      setBookings((prev) => ({
        ...prev,
        [monthKeyOf(current)]: (prev[monthKeyOf(current)] || []).map((b) => b.id === id ? { ...b, [key]: current[key] } : b),
      }));
      console.error("[bookings] toggle failed", e);
    }
  };

  /** Per-field update used by inline row controls. Optimistic + persist. */
  const setBookingField = async (id, key, value) => {
    const current = findBooking(id);
    if (!current) return;

    // Compute local patch (with billStatus → paymentDate logic preserved)
    const localPatch = { [key]: value };
    if (key === "billStatus") {
      if (value === "paid" && !current.paymentDate) {
        localPatch.paymentDate = new Date().toISOString().split("T")[0];
      }
      /* Stamp when the bill was submitted. Nothing recorded this before, so
         a submitted-but-unpaid bill had no date to show -- paymentDate stays
         empty until the money arrives.

         Only set on the FIRST move to submitted (`!current.billSubmittedAt`),
         mirroring the paymentDate rule above: re-saving must not overwrite
         the original date. Marking a bill paid also stamps it if it went
         straight to paid without passing through submitted, since a bill
         must have been raised for it to be paid at all.

         NOT cleared when moving back to not_generated -- unlike paymentDate,
         which represents money that did not arrive. A bill that was raised
         was raised; the date is a fact about what happened. */
      if ((value === "submitted" || value === "paid") && !current.billSubmittedAt) {
        localPatch.billSubmittedAt = new Date().toISOString().split("T")[0];
      }
      if (value !== "paid") {
        localPatch.paymentDate = "";
      }
    }

    setBookings((prev) => ({
      ...prev,
      [monthKeyOf(current)]: (prev[monthKeyOf(current)] || []).map((b) => b.id === id ? { ...b, ...localPatch } : b),
    }));
    try {
      await bookingsApi.update(id, localPatch);
    } catch (e) {
      // Roll back to previous value
      setBookings((prev) => ({
        ...prev,
        [monthKeyOf(current)]: (prev[monthKeyOf(current)] || []).map((b) => b.id === id ? current : b),
      }));
      console.error("[bookings] field update failed", e);
    }
  };

  /** Full-form update from the booking modal "Save". */
  const updateBooking = async (id, patch) => {
    const current = findBooking(id);
    if (!current) return;

    // Don't let cancellation fields slip through this method — use cancel/uncancel.
    // srNo stays in the patch: it is user-editable so a gap in the global
    // sequence can be corrected. Cancellation fields are still stripped --
    // those go through cancelBooking/uncancelBooking.
    const { cancelled, cancelledAt, cancelReason, id: _ignore, ...safePatch } = patch;

    setBookings((prev) => ({
      ...prev,
      [monthKeyOf(current)]: (prev[monthKeyOf(current)] || []).map((b) => b.id === id ? { ...b, ...safePatch } : b),
    }));
    try {
      const updated = await bookingsApi.update(id, safePatch);
      setBookings((prev) => ({
        ...prev,
        [monthKeyOf(current)]: (prev[monthKeyOf(current)] || []).map((b) => b.id === id ? updated : b),
      }));
    } catch (e) {
      // Roll back
      setBookings((prev) => ({
        ...prev,
        [monthKeyOf(current)]: (prev[monthKeyOf(current)] || []).map((b) => b.id === id ? current : b),
      }));
      console.error("[bookings] update failed", e);
    }
  };

  const cancelBooking = async (id, reason = "") => {
    const current = findBooking(id);
    if (!current) return;
    setBookings((prev) => ({
      ...prev,
      [monthKeyOf(current)]: (prev[monthKeyOf(current)] || []).map((b) => b.id === id
        ? { ...b, cancelled: true, cancelledAt: new Date().toISOString(), cancelReason: reason, dealStatus: "Cancelled" }
        : b),
    }));
    try {
      await bookingsApi.cancel(id, reason);
    } catch (e) {
      setBookings((prev) => ({
        ...prev,
        [monthKeyOf(current)]: (prev[monthKeyOf(current)] || []).map((b) => b.id === id ? current : b),
      }));
      console.error("[bookings] cancel failed", e);
    }
  };

  const uncancelBooking = async (id) => {
    const current = findBooking(id);
    if (!current) return;
    setBookings((prev) => ({
      ...prev,
      [monthKeyOf(current)]: (prev[monthKeyOf(current)] || []).map((b) => b.id === id
        ? { ...b, cancelled: false, cancelledAt: null, cancelReason: "", dealStatus: "Agreement Done" }
        : b),
    }));
    try {
      await bookingsApi.uncancel(id);
    } catch (e) {
      setBookings((prev) => ({
        ...prev,
        [monthKeyOf(current)]: (prev[monthKeyOf(current)] || []).map((b) => b.id === id ? current : b),
      }));
      console.error("[bookings] uncancel failed", e);
    }
  };

  // Phase 5 Bundle B — API-backed expenses (month-keyed map preserved).
  const [expenses, setExpenses] = useState({});
  const activeExpenses = expenses[billingMonth] || [];

  const reloadExpenses = useCallback(async (monthLabel) => {
    try {
      const rows = await expensesApi.listByMonth(monthLabel);
      setExpenses((prev) => ({ ...prev, [monthLabel]: rows }));
    } catch (e) {
      if (e?.status !== 403) console.warn("[expenses] reload failed", e);
    }
  }, []);

  const addExpense = async (title, amount) => {
    if (!title || !amount) return false;
    try {
      const created = await expensesApi.create({
        title, amount: Number(amount) || 0, monthLabel: billingMonth,
      });
      setExpenses((prev) => ({
        ...prev,
        [billingMonth]: [...(prev[billingMonth] || []), created],
      }));
      return true;
    } catch (e) {
      console.error("[expenses] create failed", e);
      return false;
    }
  };

  const updateExpense = async (id, patch) => {
    const current = (expenses[billingMonth] || []).find((e) => e.id === id);
    if (!current) return;
    // Optimistic
    setExpenses((prev) => ({
      ...prev,
      [billingMonth]: (prev[billingMonth] || []).map((e) => e.id === id ? { ...e, ...patch } : e),
    }));
    try {
      const updated = await expensesApi.update(id, patch);
      setExpenses((prev) => ({
        ...prev,
        [billingMonth]: (prev[billingMonth] || []).map((e) => e.id === id ? updated : e),
      }));
    } catch (e) {
      // Roll back
      setExpenses((prev) => ({
        ...prev,
        [billingMonth]: (prev[billingMonth] || []).map((e) => e.id === id ? current : e),
      }));
      console.error("[expenses] update failed", e);
    }
  };

  const deleteExpense = async (id) => {
    const current = (expenses[billingMonth] || []).find((e) => e.id === id);
    if (!current) return;
    setExpenses((prev) => ({
      ...prev,
      [billingMonth]: (prev[billingMonth] || []).filter((e) => e.id !== id),
    }));
    try {
      await expensesApi.delete(id);
    } catch (e) {
      // Roll back
      setExpenses((prev) => ({
        ...prev,
        [billingMonth]: [...(prev[billingMonth] || []), current],
      }));
      console.error("[expenses] delete failed", e);
    }
  };

  // Phase 5 Bundle B — API-backed GST payments (flat list preserved).
  const [gstPayments, setGstPayments] = useState([]);

  const reloadGstPayments = useCallback(async () => {
    try {
      const rows = await gstApi.list();
      setGstPayments(rows);
    } catch (e) {
      if (e?.status !== 403) console.warn("[gst] reload failed", e);
    }
  }, []);

  const addGstPayment = async (entry) => {
    try {
      const created = await gstApi.create(entry);
      setGstPayments((prev) => [created, ...prev]);
      return created;
    } catch (e) {
      console.error("[gst] create failed", e);
      throw e;
    }
  };

  const updateGstPayment = async (id, patch) => {
    const current = gstPayments.find((p) => p.id === id);
    if (!current) return;
    // Optimistic
    setGstPayments((prev) => prev.map((p) => p.id === id ? { ...p, ...patch } : p));
    try {
      const updated = await gstApi.update(id, patch);
      setGstPayments((prev) => prev.map((p) => p.id === id ? updated : p));
    } catch (e) {
      setGstPayments((prev) => prev.map((p) => p.id === id ? current : p));
      console.error("[gst] update failed", e);
    }
  };

  const deleteGstPayment = async (id) => {
    const current = gstPayments.find((p) => p.id === id);
    if (!current) return;
    setGstPayments((prev) => prev.filter((p) => p.id !== id));
    try {
      await gstApi.delete(id);
    } catch (e) {
      setGstPayments((prev) => [current, ...prev]);
      console.error("[gst] delete failed", e);
    }
  };
  // Phase 5 Bundle B — auto-reload expenses + GST
  useEffect(() => {
    reloadExpenses(billingMonth);
  }, [billingMonth, reloadExpenses]);

  useEffect(() => {
    reloadGstPayments();
  }, [reloadGstPayments]);

  const [billCounters, setBillCounters] = useState({ builder: 1, fos: 1 });
  const peekNextBillNumber = (type) => String(billCounters[type] || 1).padStart(3, "0");
  const bumpBillNumber = (type) => {
    setBillCounters((prev) => ({ ...prev, [type]: (prev[type] || 1) + 1 }));
  };

  const monthlyLogByPerson = useMemo(() => {
    const [mname, yr] = billingMonth.split(" ");
    const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const mi = months.indexOf(mname);
    const prefix = `${yr}-${String(mi + 1).padStart(2, "0")}-`;
    const out = {};
    allSalesPeople.forEach((s) => {
      out[s.name] = (attendanceLog[s.name] || []).filter((r) => r.date.startsWith(prefix));
    });
    return out;
  }, [attendanceLog, billingMonth, allSalesPeople]);

  const builderKPIs = useMemo(() => {
    let total = 0, paid = 0;
    liveBookings.forEach((b) => {
      const v = parseFloat(b.builderIncentive) || 0;
      total += v; if (b.builderPaid) paid += v;
    });
    return { total, paid, balance: total - paid };
  }, [liveBookings]);
  const sdKPIs = useMemo(() => {
    let total = 0, paid = 0;
    liveBookings.forEach((b) => {
      const v = parseFloat(b.skyDreamIncentive) || 0;
      total += v; if (b.skyDreamPaid) paid += v;
    });
    return { total, paid, balance: total - paid };
  }, [liveBookings]);

  const staffSalaries = useMemo(() => {
    // refreshEmployees fetches with active:"all" so historical lookups (lead
    // assignee names, past bookings) still resolve people who have since been
    // deactivated. Payroll must NOT inherit that — a deactivated employee
    // should stop costing money. Filter at this boundary rather than at the
    // fetch, so only payroll is affected.
    // Payroll follows users.active ONLY — the flag Settings -> Users toggles.
    //
    // employees.active is a separate column that Settings never writes, and in
    // practice the two are inversely correlated: every reactivated user had
    // emp_active=false and vice versa. Honouring both meant one person survived
    // out of ten. Since Settings -> Users is the only control an admin has, it
    // is the one that decides who is on payroll.
    //
    // employees.active still governs the Employees screen's own archive action;
    // it just no longer silently removes people from payroll.
    return allSalesPeople.filter((m) => m.userActive !== false).map((m) => {
      /* joiningDate: a day before someone joined is not an absence. Only
         affects anyone who joined AFTER the launch month -- everyone else
         is already covered by the launch-month guard, so their figures
         cannot move. */
      const summary = summariseAttendance(monthlyLogByPerson[m.name] || [], billingMonth, officeTiming.weekendDay ?? 0, officeTiming.endHHMM, m.profile?.joiningDate);
      // Phase 5 Bundle A — use the employee's actual baseSalary from API.
      //
      // NOTE: this used to end in `|| undefined`, intending "not set, use the
      // BASE_SALARY default". But computePayroll takes baseSalary as a DEFAULT
      // PARAMETER, and defaults fire on undefined — so every employee with no
      // configured salary silently drew a phantom BASE_SALARY (Rs 20,000).
      // With 9 of 10 employees unconfigured that invented ~Rs 1.8L of payroll
      // expense per month and pushed P&L into a permanent deficit.
      //
      // Passing an explicit 0 bypasses the default: no salary configured means
      // no salary cost, until someone sets a real figure on the Employees
      // screen. Correct by default rather than wrong by default.
      /* A month with NO ACTIVITY costs nothing.
         
         computePayroll only ever DEDUCTS from baseSalary, so a month with
         no attendance rows has nothing to deduct and returns the full
         salary. Selecting August 2026 -- before the CRM existed and before
         anyone was on these books -- therefore showed 40,000 of payroll,
         and the same figure appeared in the monthly P&L.
         
         `marked` (attendance.js:165) counts stored rows only; autoAbsent is
         added after it, fires solely for days that have already elapsed,
         and never before the launch month. Neither is present for a
         pre-launch or future month, so the pair is the test for "nothing
         happened here".
         
         Passing 0 rather than skipping the employee keeps the row on the
         Attendance & Payroll screen with every figure consistently zero --
         base, per-day, deductions and net -- exactly as an employee with no
         configured salary already renders. The same guard runs in
         yearlyPnl so the two screens cannot disagree. */
      const hasActivity = summary.marked > 0 || summary.autoAbsent > 0;
      /* The rate in force FOR THIS MONTH. salaryFor returns null while
         history is loading -- hold the current figure then, so nothing
         flashes zero -- and 0 once loaded with no applicable row, which is
         the honest answer for someone not on the books that month. */
      const histSalary = salaryFor(salaryRows, m.id, billingMonth);
      const empSalary = !hasActivity
        ? 0
        : histSalary === null
          ? (Number(m.profile?.salary ?? m.baseSalary ?? 0) || 0)
          : histSalary;
      const payroll = computePayroll(summary, empSalary);
      // Phase 5 Bundle A — match bookings by executiveId (preferred) or executiveName.
      const matchesEmp = (b) => (b.executiveId && b.executiveId === (m.userId || m.id)) || b.executive === m.name || b.executiveName === m.name;
      const skydreamsIncentivePaid = liveBookings
        .filter((b) => matchesEmp(b) && b.skyDreamPaid)
        .reduce((acc, b) => acc + (parseFloat(b.skyDreamIncentive) || 0), 0);
      const skydreamsIncentivePending = liveBookings
        .filter((b) => matchesEmp(b) && !b.skyDreamPaid)
        .reduce((acc, b) => acc + (parseFloat(b.skyDreamIncentive) || 0), 0);
      const builderIncentivePaid = liveBookings
        .filter((b) => matchesEmp(b) && b.builderPaid)
        .reduce((acc, b) => acc + (parseFloat(b.builderIncentive) || 0), 0);
      const builderIncentivePending = liveBookings
        .filter((b) => matchesEmp(b) && !b.builderPaid)
        .reduce((acc, b) => acc + (parseFloat(b.builderIncentive) || 0), 0);
      const advanceTotal = activeAdvances
        .filter((a) => (a.employeeId && a.employeeId === m.id) || a.employeeName === m.name || a.executive === m.name)
        .reduce((acc, a) => acc + (parseFloat(a.amount) || 0), 0);
      const netAfterAdvance = Math.max(0, payroll.net - advanceTotal);
      const advanceShortfall = Math.max(0, advanceTotal - payroll.net);
      // Total earning = SALARY ONLY. Incentives keep their own columns in the
      // payroll table but are deliberately excluded from this figure: salary is
      // a recurring committed cost, incentives are variable and paid against
      // specific bookings. Rolling them together made the monthly wage bill look
      // larger than it is.
      // P&L is unaffected — it already sums calculatedSalary and
      // skydreamsIncentivePaid into separate buckets (see pnl useMemo).
      const totalEarning = netAfterAdvance;
      let dominant = "present";
      if (summary.absent > 0) dominant = "absent";
      else if (summary.half_day > 0) dominant = "half_day";
      else if (summary.leave > 0) dominant = "leave";
      return {
        ...m, baseSalary: payroll.baseSalary, calculatedSalary: payroll.net,
        advanceTotal, netAfterAdvance, advanceShortfall,
        skydreamsIncentivePaid, skydreamsIncentivePending,
        builderIncentivePaid, builderIncentivePending, totalEarning,
        status: dominant, summary, payroll,
      };
    });
  }, [allSalesPeople, monthlyLogByPerson, billingMonth, officeTiming.weekendDay, activeAdvances, liveBookings, salaryRows]);

  const pnl = useMemo(() => {
    let income = 0;
    liveBookings.forEach((b) => { if (b.billStatus === "paid") income += parseFloat(b.brokerageAmount) || 0; });
    const payroll = staffSalaries.reduce((a, s) => a + s.calculatedSalary, 0);
    const incentivePayout = staffSalaries.reduce((a, s) => a + s.skydreamsIncentivePaid, 0);
    const misc = activeExpenses.reduce((a, e) => a + (parseFloat(e.amount) || 0), 0);
    const expense = payroll + incentivePayout + misc;
    const net = income - expense;
    return { income, expense, net, isLoss: net < 0, payroll, incentivePayout, misc };
  }, [liveBookings, staffSalaries, activeExpenses]);

  const ledgerKPIs = useMemo(() => {
    let totalBookings = liveBookings.length;
    let totalRevenue = 0, billSubmitted = 0, paymentReceived = 0;
    liveBookings.forEach((b) => {
      const bro = parseFloat(b.brokerageAmount) || 0;
      totalRevenue += bro;
      if (b.billStatus === "submitted") billSubmitted += bro;
      if (b.billStatus === "paid") paymentReceived += bro;
    });
    return { totalBookings, totalRevenue, billSubmitted, paymentReceived, balance: totalRevenue - paymentReceived };
  }, [liveBookings]);

  /* Yearly P&L across the FINANCIAL year, 1 April - 31 March.
     Previously mapped BILLING_MONTHS, which starts at the app's launch
     month, so April-August 2026 were missing from FY 2026-27 entirely. */
  const yearlyPnl = useMemo(() => {
    const FY_MONTH_NAMES = ["April","May","June","July","August","September",
                            "October","November","December","January","February","March"];
    const now = new Date();
    const fyStart = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
    const fyLabels = FY_MONTH_NAMES.map((m, i) => `${m} ${i < 9 ? fyStart : fyStart + 1}`);

    const months = fyLabels.map((mLabel) => {
      const bks = (bookings[mLabel] || []).filter((b) => !b.cancelled);
      const exps = expenses[mLabel] || [];
      let inc = 0, incentivePayout = 0;
      bks.forEach((b) => {
        if (b.billStatus === "paid") inc += parseFloat(b.brokerageAmount) || 0;
        if (b.skyDreamPaid) incentivePayout += parseFloat(b.skyDreamIncentive) || 0;
      });
      /* Payroll computed from THIS month's attendance, for every month --
         not just the selected one.
         
         Two earlier versions were wrong. The first fell back to
         `allSalesPeople.length * BASE_SALARY`, which reads nobody's actual
         salary: it multiplied HEADCOUNT by a hardcoded 20,000, inventing
         80,000 of expense in every month including ones that had not
         happened. The second returned 0 for any month but the selected one,
         which stopped the invention but meant September's real payroll
         would vanish the moment the month picker moved -- and drop to zero
         entirely in October.
         
         This mirrors staffSalaries exactly: same userActive filter, same
         summariseAttendance arguments, same explicit-0 salary fallback, and
         the same `payroll.net` that staffSalaries aliases to
         calculatedSalary. Any drift between the two would put the monthly
         and yearly figures out of step on the same screen. */
      const ymPrefix = (() => {
        const [mn, yr] = String(mLabel).split(" ");
        const idx = ["January","February","March","April","May","June","July",
                     "August","September","October","November","December"].indexOf(mn);
        return idx < 0 ? null : `${yr}-${String(idx + 1).padStart(2, "0")}`;
      })();

      const monthPayroll = !ymPrefix ? 0 : allSalesPeople
        .filter((m) => m.userActive !== false)
        .reduce((sum, m) => {
          const log = (attendanceLog[m.name] || []).filter((r) => r.date.startsWith(ymPrefix));
          const summary = summariseAttendance(log, mLabel, officeTiming.weekendDay ?? 0, officeTiming.endHHMM, m.profile?.joiningDate);

          /* NO RECORDED ATTENDANCE MEANS NO PAYROLL for that month.
             
             computePayroll deducts from baseSalary, so an empty log has
             nothing to deduct and returns the FULL salary. That charged
             40,000 to April 2026 -- before the CRM existed and before these
             employees were on the books -- and to every future month of the
             FY, projecting today's headcount and today's pay forward as if
             it were fact.
             
             The test has to count autoAbsent as well as stored rows.
             `marked` (attendance.js:165) is summed BEFORE autoAbsent is
             added at 167, so an employee with no marked days but elapsed
             unmarked ones -- SKY DREAM in September 2026: 0 present, 8
             auto-absent, net 13,846 -- reads marked === 0 and would be
             skipped, putting the yearly total 13,846 below the Attendance &
             Payroll figure for the same month.
             
             autoAbsent only fires for days that have already elapsed, and
             never before the launch month, so a pre-September or future
             month still has neither and correctly contributes nothing. */
          if (summary.marked === 0 && summary.autoAbsent === 0) return sum;

          /* Per-month rate, so a rise in November cannot rewrite
             September. Mirrors staffSalaries exactly -- drift would put the
             monthly and yearly figures out of step on one screen. */
          const hist = salaryFor(salaryRows, m.id, mLabel);
          const empSalary = hist === null
            ? (Number(m.profile?.salary ?? m.baseSalary ?? 0) || 0)
            : hist;
          return sum + computePayroll(summary, empSalary).net;
        }, 0);
      const misc = exps.reduce((a, e) => a + (parseFloat(e.amount) || 0), 0);
      const expense = monthPayroll + incentivePayout + misc;
      return { month: mLabel, income: inc, expense, net: inc - expense };
    });
    const totals = months.reduce((acc, x) => ({
      income: acc.income + x.income, expense: acc.expense + x.expense, net: acc.net + x.net,
    }), { income: 0, expense: 0, net: 0 });
    return { months, totals };
  }, [bookings, expenses, allSalesPeople, attendanceLog, officeTiming, salaryRows]);

  // ─── Phase 4 — site settings (API-backed) ──────────────────────────
  // Exposes BOTH the legacy nested shape used by existing customer-site-
  // adjacent admin code AND the flat API shape used by SiteContactSettings.
  // Two consumer patterns — one source of truth on the backend.
  const [siteSettingsRaw, setSiteSettingsRaw] = useState(null);

  const reloadSiteContact = useCallback(async () => {
    try {
      const s = await siteSettingsApi.get();
      setSiteSettingsRaw(s);
    } catch (e) {
      console.error("Failed to load site settings:", e);
    }
  }, []);

  // Legacy nested shape — for existing components reading siteContact.phones.primary etc.
  const siteContact = useMemo(() => ({
    // raw API fields (used by new SiteContactSettings form)
    ...(siteSettingsRaw || {}),
    // legacy nested fields (used by older mock-style consumers)
    phones: {
      primary: siteSettingsRaw?.primaryPhone || "9730302843",
      secondary: siteSettingsRaw?.secondaryPhone || "9890122755",
    },
    whatsapp: {
      footer: siteSettingsRaw?.whatsappFooter || "9730302843",
      floating: siteSettingsRaw?.whatsappFloating || "9890122755",
    },
    email: siteSettingsRaw?.email || "contact@abhighar.com",
    hours: siteSettingsRaw?.hours || "Mon – Sun · 10 AM – 8 PM",
    advisor: {
      name: siteSettingsRaw?.defaultAdvisorName || "Sarika",
      initials: siteSettingsRaw?.defaultAdvisorInitials || "SK",
      role: siteSettingsRaw?.defaultAdvisorRole || "Senior Advisor",
    },
  }), [siteSettingsRaw]);

  // setSiteContact accepts either shape. If passed nested (legacy),
  // converts to flat. If passed flat (from new form), persists directly.
  const setSiteContact = async (next) => {
    let payload = next;
    if (next?.phones || next?.whatsapp || next?.advisor) {
      // Legacy nested form — flatten before sending
      payload = {
        primaryPhone: next.phones?.primary,
        secondaryPhone: next.phones?.secondary,
        whatsappFooter: next.whatsapp?.footer,
        whatsappFloating: next.whatsapp?.floating,
        email: next.email,
        hours: next.hours,
        defaultAdvisorName: next.advisor?.name,
        defaultAdvisorInitials: next.advisor?.initials,
        defaultAdvisorRole: next.advisor?.role,
      };
    }
    const updated = await siteSettingsApi.update(payload);
    setSiteSettingsRaw(updated);
    // F4 fix: re-fetch in background to guarantee fresh state (defensive)
    // Note: doesn't await — UI uses `updated` immediately for snappiness
    siteSettingsApi.get().then((s) => s && setSiteSettingsRaw(s)).catch(() => {});
    return updated;
  };

  const value = {
    // Phase 3: API-backed
    cities, addCity, updateCity, toggleCityActive, deleteCity,
    localities, addLocality, updateLocality, toggleLocalityActive, deleteLocality,
    builders, addBuilder, updateBuilder, deleteBuilder, reorderBuilder,
    properties, addProperty, updateProperty, deleteProperty, duplicateProperty,
    mastersLoading, propertiesLoading, refreshMasters, refreshProperties,
    dirtyForm, setDirtyForm,

    allSalesPeople, upsertSalesPerson, deleteSalesPerson, isSeededSalesPerson,
    attendanceLog, markAttendance, monthlyLogByPerson, staffSalaries, clockInSelf, clockOutSelf, markAbsentSelf,
    officeTiming, setOfficeTiming,
    billingMonth, setBillingMonth, month: billingMonth, setMonth: setBillingMonth,
    salaryAdvances: activeAdvances, addSalaryAdvance, updateSalaryAdvance, deleteSalaryAdvance,
    perfTargets, setPerfTarget, commitPerfTargets, commitAllPerfTargets,

    leadsData, leadsLoading, leadsError, refreshLeads,
    addLead, updateLead, deleteLead, addLeadNote, bulkLeadAction,
    importLeadsCsv, exportLeadsCsv,
    activeEmployees, refreshEmployees,

    statusVersion, bumpStatusVersion, refreshLeadStatuses, leadStatusesApi,
    interiorLeads, interiorLeadsLoaded, reloadInteriorLeads,
    addInteriorLead, updateInteriorLead, deleteInteriorLead,
    posts, postsLoaded, reloadPosts, addPost, updatePost, deletePost,
    // Phase 4 — site settings new field names
    siteSettings: siteSettingsRaw,
    siteSettingsLoaded: !!siteSettingsRaw,
    reloadSiteSettings: reloadSiteContact,
    updateSiteSettings: setSiteContact,
    bookings, activeBookings, liveBookings, cancelledBookings, reloadBookings,
    addBooking, updateBooking, cancelBooking, uncancelBooking, toggleBookingFlag, setBookingField,
    expenses, activeExpenses, addExpense, updateExpense, deleteExpense,
    gstPayments, addGstPayment, updateGstPayment, deleteGstPayment,
    billCounters, peekNextBillNumber, bumpBillNumber,
    builderKPIs, sdKPIs, pnl, ledgerKPIs, yearlyPnl,
    siteContact, setSiteContact,

    // Phase 4.5 — Interior CMS
    interiorDesigners, interiorDesignersLoaded, reloadInteriorDesigners,
    addInteriorDesigner, updateInteriorDesigner, deleteInteriorDesigner,

    interiorPackages, interiorPackagesLoaded, reloadInteriorPackages,
    addInteriorPackage, updateInteriorPackage, deleteInteriorPackage,

    interiorPortfolio, interiorPortfolioLoaded, reloadInteriorPortfolio,
    addInteriorPortfolio, updateInteriorPortfolio, deleteInteriorPortfolio,

    interiorPageContent, interiorPageContentLoaded,
    reloadInteriorPageContent, updateInteriorPageContent,
  };

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error("useAppData must be used inside AppDataProvider");
  return ctx;
}
